export type FundInactivationReason = 'transfer' | 'closure' | 'other';

export type FundActivationReason = 'transfer' | 'other';

export type ApiFundInactivationReason =
  | 'transferencia'
  | 'outro'
  | 'encerramento'
  | string;

export interface FundAdministradorDetalhes {
  cnpj: string;
  email: string | null;
  celular: string | null;
  contato: string | null;
  telefone: string | null;
  ddiCelular: string | null;
  ddiTelefone: string | null;
  razaoSocial: string;
}

export interface FundTransferDestination {
  razaoSocial: string;
  cnpj: string;
  dataTransferencia: string | null;
  dataAssembleia: string | null;
  comentario: string;
  dataEvento: string;
}

export interface FundTransferEventData {
  descricao: string | null;
  administrador: string;
  dataAssembleia: string | null;
  dataTransferencia: string | null;
  administrador_detalhes?: FundAdministradorDetalhes;
}

export interface FundTransferEvent {
  direction: string;
  transfer_type: string;
  transfer_type_label: string;
  is_transfer_in: boolean;
  is_transfer_out: boolean;
  status?: string | null;
  at: string;
  comment: string;
  administrador: string;
  administrador_detalhes?: FundAdministradorDetalhes;
  transfer_description: string;
  transfer_date: string | null;
  assembly_date: string | null;
  last_company_document?: string;
  last_company_name?: string;
  transfer?: FundTransferEventData;
}

export interface FundStatusLog {
  event: string;
  status: string;
  status_display: string;
  at: string;
  comment: string;
  reason: ApiFundInactivationReason;
  reason_label: string;
  destination?: FundTransferDestination | null;
  transfer?: FundTransferEvent | null;
  transfer_events?: FundTransferEvent[];
  transfer_direction?: string | null;
  transfer_type?: string | null;
  transfer_type_label?: string | null;
  administrador?: string;
  transfer_description?: string;
  transfer_comment?: string | null;
  transfer_date?: string | null;
  assembly_date?: string | null;
  last_company_name?: string;
  last_company_document?: string;
  administrator_name?: string | null;
  administrator_document?: string | null;
  administrator_contact?: string | null;
  administrator_email?: string | null;
  administrator_phone?: string | null;
  administrator_phone_ddi?: string | null;
  administrator_mobile?: string | null;
  administrator_mobile_ddi?: string | null;
}

export interface FundInactivationApi {
  reason: ApiFundInactivationReason;
  reason_label: string;
  date: string;
  comment: string;
  status: string;
  status_display: string;
  destination: FundTransferDestination | null;
  transfer: FundTransferEvent | null;
  transfer_events: FundTransferEvent[];
  transfer_direction: string | null;
  transfer_type: string | null;
  transfer_type_label: string | null;
  administrador: string;
  transfer_description: string;
  transfer_at: string | null;
  transfer_comment: string | null;
  transfer_date: string | null;
  assembly_date: string | null;
  last_company_name?: string | null;
  last_company_document?: string | null;
}

export type FundWithInactivation = {
  status?: string;
  inactivation?: FundInactivationApi | null;
  status_logs?: FundStatusLog[] | null;
  transfer_events?: FundTransferEvent[];
};

export interface FundInactivationView {
  reason: FundInactivationReason | string;
  reasonLabel: string;
  comment: string;
  date: string;
  transferDate?: string;
  assemblyDate?: string;
  administrador?: string;
  administradorCnpj?: string;
  transfer?: FundTransferData;
}

export interface FundTransferData {
  transferDate: string;
  assemblyDate: string;
  cnpj: string;
  companyName: string;
  contactName: string;
  email: string;
  phoneDdi: string;
  phone: string;
  mobileDdi: string;
  mobile: string;
}

export const FUND_INACTIVATION_REASON_LABELS: Record<
  FundInactivationReason,
  string
> = {
  transfer: 'Transferido para outro administrador',
  closure: 'Encerramento do fundo',
  other: 'Outro motivo',
};

const API_REASON_TO_INTERNAL: Record<string, FundInactivationReason> = {
  transferencia: 'transfer',
  outro: 'other',
  encerramento: 'closure',
};

export const FUND_ACTIVATION_REASON_LABELS: Record<
  FundActivationReason,
  string
> = {
  transfer: 'Recebimento de outro administrador',
  other: 'Outro motivo',
};

export const INTERNAL_REASON_TO_API: Record<FundInactivationReason, string> = {
  transfer: 'transferencia',
  closure: 'encerramento',
  other: 'outro',
};

export function mapApiReasonToInternal(
  reason?: ApiFundInactivationReason | FundInactivationReason | null
): FundInactivationReason | string {
  if (!reason) return 'other';
  if (reason in FUND_INACTIVATION_REASON_LABELS) {
    return reason as FundInactivationReason;
  }
  return API_REASON_TO_INTERNAL[reason] ?? reason;
}

export function isFundOperationallyActive(
  fund?: FundWithInactivation | null
): boolean {
  if (!fund) return true;
  if (fund.status) return fund.status === 'active';
  return resolveFundInactivationApi(fund)?.status !== 'inactive';
}

export function resolveFundInactivationApi(
  fund?: FundWithInactivation | null
): FundInactivationApi | null {
  if (!fund) return null;

  const logs = fund.status_logs;
  if (logs?.length) {
    const inactivationLog =
      pickLatestStatusLog(logs.filter((log) => log.event === 'inactivation')) ??
      pickLatestStatusLog(
        logs.filter(
          (log) =>
            log.status === 'inactive' &&
            log.event !== 'transfer-out' &&
            log.event !== 'transfer-in'
        )
      );

    if (inactivationLog) {
      const transferLog = pickLatestStatusLog(
        logs.filter(
          (log) =>
            log.event === 'transfer-out' ||
            log.transfer_type === 'transfer-out'
        )
      );

      return mapStatusLogToInactivationApi(inactivationLog, transferLog);
    }
  }

  return fund.inactivation ?? null;
}

function pickLatestStatusLog(
  logs: FundStatusLog[]
): FundStatusLog | null {
  if (!logs.length) return null;

  return [...logs].sort((a, b) => {
    const aTime = new Date(a.at).getTime();
    const bTime = new Date(b.at).getTime();
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
  })[0];
}

function mapStatusLogToInactivationApi(
  log: FundStatusLog,
  transferLog?: FundStatusLog | null
): FundInactivationApi {
  const isTransferOut = Boolean(
    transferLog ||
      log.event === 'transfer-out' ||
      log.transfer_type === 'transfer-out' ||
      mapApiReasonToInternal(log.reason) === 'transfer'
  );

  const companyName =
    transferLog?.last_company_name ??
    log.last_company_name ??
    log.administrador ??
    '';
  const companyDocument =
    transferLog?.last_company_document ?? log.last_company_document ?? '';

  return {
    reason: log.reason,
    reason_label: log.reason_label,
    date: log.at,
    comment: log.comment ?? '',
    status: log.status,
    status_display: log.status_display,
    destination:
      log.destination ??
      (companyName || companyDocument
        ? {
            razaoSocial: companyName,
            cnpj: companyDocument,
            dataTransferencia:
              transferLog?.transfer_date ?? log.transfer_date ?? null,
            dataAssembleia:
              transferLog?.assembly_date ?? log.assembly_date ?? null,
            comentario: transferLog?.comment ?? log.comment ?? '',
            dataEvento: transferLog?.at ?? log.at,
          }
        : null),
    transfer: log.transfer ?? null,
    transfer_events: log.transfer_events ?? [],
    transfer_direction: isTransferOut
      ? 'transfer-out'
      : log.transfer_direction ?? null,
    transfer_type:
      transferLog?.transfer_type ?? log.transfer_type ?? null,
    transfer_type_label:
      transferLog?.transfer_type_label ?? log.transfer_type_label ?? null,
    administrador: companyName,
    transfer_description: log.transfer_description ?? '',
    transfer_at: log.at,
    transfer_comment: log.transfer_comment ?? log.comment ?? null,
    transfer_date: transferLog?.transfer_date ?? log.transfer_date ?? null,
    assembly_date: transferLog?.assembly_date ?? log.assembly_date ?? null,
    last_company_name: companyName || null,
    last_company_document: companyDocument || null,
  };
}

export function mapFundInactivation(
  inactivation?: FundInactivationApi | null
): FundInactivationView | null {
  if (!inactivation) return null;

  const administradorDetalhes =
    inactivation.transfer?.administrador_detalhes ??
    inactivation.transfer?.transfer?.administrador_detalhes;

  const destination = inactivation.destination;

  const transfer: FundTransferData | undefined =
    inactivation.reason === 'transferencia' ||
    mapApiReasonToInternal(inactivation.reason) === 'transfer' ||
    inactivation.transfer_direction === 'transfer-out'
      ? {
          transferDate:
            inactivation.transfer_date ??
            inactivation.transfer?.transfer_date ??
            inactivation.transfer?.transfer?.dataTransferencia ??
            destination?.dataTransferencia ??
            '',
          assemblyDate:
            inactivation.assembly_date ??
            inactivation.transfer?.assembly_date ??
            inactivation.transfer?.transfer?.dataAssembleia ??
            destination?.dataAssembleia ??
            '',
          cnpj:
            inactivation.last_company_document ??
            administradorDetalhes?.cnpj ??
            destination?.cnpj ??
            '',
          companyName:
            inactivation.last_company_name ??
            administradorDetalhes?.razaoSocial ??
            destination?.razaoSocial ??
            inactivation.transfer?.administrador ??
            inactivation.administrador ??
            '',
          contactName: administradorDetalhes?.contato ?? '',
          email: administradorDetalhes?.email ?? '',
          phoneDdi: administradorDetalhes?.ddiTelefone ?? '+55',
          phone: administradorDetalhes?.telefone ?? '',
          mobileDdi: administradorDetalhes?.ddiCelular ?? '+55',
          mobile: administradorDetalhes?.celular ?? '',
        }
      : undefined;

  return {
    reason: mapApiReasonToInternal(inactivation.reason),
    reasonLabel: inactivation.reason_label,
    comment: inactivation.comment ?? inactivation.transfer_comment ?? '',
    date: formatInactivationDate(inactivation.date ?? inactivation.transfer_at),
    transferDate: inactivation.transfer_date ?? transfer?.transferDate,
    assemblyDate: inactivation.assembly_date ?? transfer?.assemblyDate,
    administrador:
      inactivation.last_company_name ||
      inactivation.administrador ||
      administradorDetalhes?.razaoSocial ||
      destination?.razaoSocial ||
      inactivation.transfer?.administrador ||
      '',
    administradorCnpj:
      inactivation.last_company_document ??
      administradorDetalhes?.cnpj ??
      destination?.cnpj ??
      '',
    transfer,
  };
}

export function mapFundInactivationFromFund(
  fund?: FundWithInactivation | null
): FundInactivationView | null {
  return mapFundInactivation(resolveFundInactivationApi(fund));
}

export function isPendingStatus(status?: string | null): boolean {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase();
  return normalized === 'pending' || normalized === 'pendente';
}

export function isAwaitingConfirmationStatus(value?: string | null): boolean {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_');
  return (
    normalized === 'awaiting_confirmation' ||
    normalized === 'transfer_confirmation_pending' ||
    normalized.includes('awaiting_confirmation') ||
    normalized.includes('confirmation_pending')
  );
}

function itemHasAwaitingConfirmation(item?: {
  status?: string | null;
  status_display?: string | null;
} | null): boolean {
  if (!item) return false;
  return (
    isAwaitingConfirmationStatus(item.status) ||
    isAwaitingConfirmationStatus(item.status_display)
  );
}

function isTransferOutLike(item?: {
  event?: string | null;
  transfer_type?: string | null;
  direction?: string | null;
  is_transfer_out?: boolean;
} | null): boolean {
  if (!item) return false;
  return (
    item.event === 'transfer-out' ||
    item.transfer_type === 'transfer-out' ||
    item.direction === 'transfer-out' ||
    item.is_transfer_out === true
  );
}

function isTransferInLike(item?: {
  event?: string | null;
  transfer_type?: string | null;
  direction?: string | null;
  is_transfer_in?: boolean;
} | null): boolean {
  if (!item) return false;
  return (
    item.event === 'transfer-in' ||
    item.transfer_type === 'transfer-in' ||
    item.direction === 'transfer-in' ||
    item.is_transfer_in === true
  );
}

export function findPendingTransferOutLog(
  fund?: FundWithInactivation | null
): FundStatusLog | FundTransferEvent | null {
  if (!fund) return null;

  const pendingLog = fund.status_logs?.find(
    (log) => isTransferOutLike(log) && isPendingStatus(log.status)
  );
  if (pendingLog) return pendingLog;

  const pendingEvent = fund.transfer_events?.find(
    (event) => isTransferOutLike(event) && isPendingStatus(event.status)
  );
  return pendingEvent ?? null;
}

export function hasPendingTransferOut(
  fund?: FundWithInactivation | null
): boolean {
  return Boolean(findPendingTransferOutLog(fund));
}

export function findAwaitingConfirmationTransferOutLog(
  fund?: FundWithInactivation | null
): FundStatusLog | FundTransferEvent | null {
  if (!fund) return null;

  const awaitingLog = fund.status_logs?.find(
    (log) => isTransferOutLike(log) && itemHasAwaitingConfirmation(log)
  );
  if (awaitingLog) return awaitingLog;

  const awaitingEvent = fund.transfer_events?.find(
    (event) => isTransferOutLike(event) && itemHasAwaitingConfirmation(event)
  );
  return awaitingEvent ?? null;
}

export function hasAwaitingConfirmationTransferOut(
  fund?: FundWithInactivation | null
): boolean {
  if (!fund) return false;

  const inactivation = resolveFundInactivationApi(fund);
  if (
    itemHasAwaitingConfirmation(inactivation) ||
    isAwaitingConfirmationStatus((fund as { transfer_status?: string }).transfer_status)
  ) {
    return true;
  }

  return Boolean(findAwaitingConfirmationTransferOutLog(fund));
}

export function findActiveScheduledTransferOutLog(
  fund?: FundWithInactivation | null
): FundStatusLog | FundTransferEvent | null {
  return (
    findPendingTransferOutLog(fund) ??
    findAwaitingConfirmationTransferOutLog(fund)
  );
}

export function mapPendingTransferOutPrefill(
  fund?: FundWithInactivation | null
): {
  type: string;
  reasonKind: 'transfer';
  hasAdministratorFields: boolean;
  transferDate: string;
  assemblyDate: string;
  observation: string;
  administrator: {
    razaoSocial: string;
    cnpj: string;
    contato: string;
    email: string;
    telefone: string;
    celular: string;
  };
} | null {
  const log = findActiveScheduledTransferOutLog(fund);
  if (!log) return null;

  const genericComments = new Set([
    'Transferência operacional',
    'Recebimento operacional',
    'Reativação por transferência administrativa.',
  ]);
  const comment = String(
    log.comment ?? (log as FundStatusLog).transfer_comment ?? ''
  ).trim();
  const statusLog = log as FundStatusLog;

  return {
    type: 'Inativado',
    reasonKind: 'transfer',
    hasAdministratorFields: true,
    transferDate: formatDateForPrefill(log.transfer_date ?? log.at),
    assemblyDate: formatDateForPrefill(log.assembly_date),
    observation: !comment || genericComments.has(comment) ? '' : comment,
    administrator: {
      razaoSocial: String(
        statusLog.administrator_name ??
          log.last_company_name ??
          log.administrador ??
          ''
      ).trim(),
      cnpj: String(
        statusLog.administrator_document ?? log.last_company_document ?? ''
      ).trim(),
      contato: String(statusLog.administrator_contact ?? '').trim(),
      email: String(statusLog.administrator_email ?? '').trim(),
      telefone: formatPhonePrefill(
        statusLog.administrator_phone_ddi,
        statusLog.administrator_phone
      ),
      celular: formatPhonePrefill(
        statusLog.administrator_mobile_ddi,
        statusLog.administrator_mobile
      ),
    },
  };
}

function formatDateForPrefill(value?: string | null): string {
  const formatted = formatInactivationDate(value);
  return !formatted || formatted === '-' ? '' : formatted;
}

function formatPhonePrefill(
  ddi?: string | null,
  number?: string | null
): string {
  const normalizedNumber = String(number ?? '').trim();
  if (!normalizedNumber) return '';

  const normalizedDdi = String(ddi ?? '')
    .trim()
    .replace(/^\+/, '');
  return normalizedDdi
    ? `+${normalizedDdi} ${normalizedNumber}`
    : normalizedNumber;
}

export function getFundTransferTags(
  fund?: FundWithInactivation | null
): string[] {
  if (!fund) return [];

  const inactivation = resolveFundInactivationApi(fund);
  const events =
    inactivation?.transfer_events?.length
      ? inactivation.transfer_events
      : fund.transfer_events ?? [];

  const tags = new Set<string>();
  let hasPendingOut = hasPendingTransferOut(fund);
  let hasAwaitingConfirmation = hasAwaitingConfirmationTransferOut(fund);

  events.forEach((event) => {
    if (isTransferInLike(event)) {
      tags.add('Recebido');
    } else if (isTransferOutLike(event)) {
      if (itemHasAwaitingConfirmation(event)) hasAwaitingConfirmation = true;
      else if (isPendingStatus(event.status)) hasPendingOut = true;
      else tags.add('Transferido');
    } else if (event.transfer_type_label) {
      tags.add(event.transfer_type_label);
    }
  });

  fund.status_logs?.forEach((log) => {
    if (isTransferInLike(log)) {
      tags.add('Recebido');
    } else if (isTransferOutLike(log)) {
      if (itemHasAwaitingConfirmation(log)) hasAwaitingConfirmation = true;
      else if (isPendingStatus(log.status)) hasPendingOut = true;
      else tags.add('Transferido');
    } else if (log.transfer_type_label) {
      tags.add(log.transfer_type_label);
    }
  });

  if (hasAwaitingConfirmation) {
    tags.delete('Transferido');
    tags.delete('Transferência agendada');
    tags.add('Transferência a confirmar');
  } else if (hasPendingOut) {
    tags.delete('Transferido');
    tags.add('Transferência agendada');
  }

  return Array.from(tags);
}

export function formatInactivationDate(dateStr?: string | null): string {
  if (!dateStr) return '-';

  const normalized = String(dateStr).trim();
  if (!normalized) return '-';

  if (normalized.includes('/') && !normalized.includes('T')) {
    return normalized.split(' ')[0];
  }

  const isoDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  }

  try {
    const date = new Date(normalized);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

export function parseDisplayDate(value?: string | null): Date | null {
  const normalized = String(value ?? '').trim();
  if (!normalized || normalized === '--' || normalized === '-') return null;
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) return null;

  const [day, month, year] = normalized.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

export function isFutureTransferDate(value?: string | null): boolean {
  const date = parseDisplayDate(value);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() > today.getTime();
}

export function getTransferDestinationDisplay(
  inactivation?: FundInactivationView | null
): string {
  if (!inactivation) return '-';

  const name = inactivation.administrador?.trim();
  const cnpj = inactivation.administradorCnpj?.trim();

  if (name && cnpj) return `${name} • ${cnpj}`;
  if (name) return name;
  if (cnpj) return cnpj;
  return '-';
}

export function getInactivationReasonLabel(
  reason?: FundInactivationReason | string | null,
  reasonLabel?: string | null
): string {
  if (reasonLabel?.trim()) return reasonLabel.trim();
  const mapped = mapApiReasonToInternal(reason);
  if (mapped in FUND_INACTIVATION_REASON_LABELS) {
    return FUND_INACTIVATION_REASON_LABELS[mapped as FundInactivationReason];
  }
  return reason?.trim() || '-';
}

export function getFundTransferTypeLabel(fund?: {
  transfer_type_label?: string | null;
  transfer_events?: FundTransferEvent[];
  inactivation?: FundInactivationApi | null;
  status_logs?: FundStatusLog[] | null;
} | null): string {
  if (!fund) return '--';

  if (fund.transfer_type_label) return fund.transfer_type_label;

  const inactivation = resolveFundInactivationApi(fund);
  const event =
    fund.transfer_events?.[0] ?? inactivation?.transfer_events?.[0];

  if (event?.transfer_type_label) return event.transfer_type_label;
  if (event?.is_transfer_in || event?.direction === 'transfer-in') {
    return 'Recebido';
  }
  if (event?.is_transfer_out || event?.direction === 'transfer-out') {
    if (itemHasAwaitingConfirmation(event) || hasAwaitingConfirmationTransferOut(fund)) {
      return 'Transferência a confirmar';
    }
    return isPendingStatus(event.status) || hasPendingTransferOut(fund)
      ? 'Transferência agendada'
      : 'Transferido';
  }

  if (
    fund.status_logs?.some(
      (log) => isTransferOutLike(log) && itemHasAwaitingConfirmation(log)
    ) ||
    hasAwaitingConfirmationTransferOut(fund)
  ) {
    return 'Transferência a confirmar';
  }

  const pendingLog = fund.status_logs?.find(
    (log) => isTransferOutLike(log) && isPendingStatus(log.status)
  );
  if (pendingLog) return 'Transferência agendada';

  return '--';
}
